import React, {
	FC, useCallback, useMemo, useState,
} from 'react';

import {
	Checkbox, Counter, GetRowsParams, InfiniteScrollList, Label, LoaderBars, Menu,
} from '@aleph/shared-components';
import {
	IColumnEditor,
} from '@aleph/supergrid';
import { useLazyQuery } from '@apollo/client';
import { CommunityOptionsDocument, CommunityOptionsQueryItems } from 'wb-reddit/graphql/generated';
import millify from 'millify';

const CommunitiesColumnEditor: FC<IColumnEditor<CommunityOptionsQueryItems[]>> = (params) => {
	const { value, setValue, editorContext } = params;
	const initialValues = useMemo(() => value, []);
	const [loading, setLoading] = useState<boolean>(true);

	const [getCommunities] = useLazyQuery(CommunityOptionsDocument);

	const getRows = useCallback(async (resolverParams: GetRowsParams<string | null | undefined>) => {
		const { data } = await getCommunities({
			variables: {
				cursor:  resolverParams.metaData,
				search:  `*${editorContext?.search ?? ''}*`,
				exclude: initialValues?.map((initialValue) => initialValue.label) ?? [],
			},
		});

		return {
			metaData:    data?.options.cursor,
			data:        data?.options.items ?? [],
			hasNextPage: !!data?.options.cursor,
		};
	}, [editorContext?.search]);

	const toggleValue = (option: CommunityOptionsQueryItems) => () => {
		if (value?.some(v => v?.value === option?.value)) {
			setValue(value?.filter(v => v?.value !== option?.value));

			return;
		}

		setValue([
			...(value ?? []),
			option,
		]);
	};

	return (
		<Menu
			className="flex flex-col h-full p-[8px]"
		>
			<>
				{
					!loading
				&& <div
					className="flex whitespace-nowrap items-center
					gap-[8px] py-[8px] px-[4px]"
				>
					<div className="flex gap-[8px] flex-1 justify-between items-center">
						<div className="text-gray-600 text-[12px]">
							COMMUNITIES
						</div>

						{
							(value?.length ?? 0) > 0
							&& <Counter
								count={value?.length ?? 0}
								className="relative"
								variant="square-accent"
							/>
						}
					</div>
				</div>
				}
				<InfiniteScrollList<string | null | undefined, CommunityOptionsQueryItems>
					getRows={getRows}
					renderItem={({ isLoaderRow, data: option, props }) => {
						if (isLoaderRow) {
							return (<Menu.Item {...props}>
								<LoaderBars
									className="animate-spin"
									width={16}
									height={16}
								/>
								Loading rows...
							</Menu.Item>);
						}

						return 		(<Menu.Item
							onClick={toggleValue(option)}
							selected={value?.some(v => v?.value === option?.value)}
							alignItems="end"
							{...props}
						>
							<Label className="text-[14px] overflow-ellipsis">{option?.label}</Label>
							<Label className="text-gray-600 text-[12px] overflow-ellipsis">
								{millify(option?.subs)} subs
							</Label>
							<Checkbox checked={value?.some(v => v?.value === option?.value)} />
						</Menu.Item>);
					}}
					pinnedItems={initialValues}
					callbacks={{
						onLoading: setLoading,
					}}
				/>
			</>
		</Menu>
	);
};

export default CommunitiesColumnEditor;
